import React, { createContext, useContext, useState, useCallback } from 'react'
import { Project, projects as mockProjects } from '../data/mockData'
export type DataMode = 'mock' | 'live'
const DATA_MODE_KEY = 'vsm_data_mode'

// Default empty project to prevent crashes when no project is available
const EMPTY_PROJECT: Project = {
  id: 'empty',
  key: 'NONE',
  name: 'Sin proyecto',
  description: '',
  color: 'bg-slate-500',
  colorHex: '#64748b',
  status: 'on-hold',
  team: '',
  teamSize: 0,
  velocity: 0,
  completion: 0,
  issues: [],
  backlog: [],
  sprint: {
    id: 'no-sprint',
    name: 'Sin sprint',
    goal: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    status: 'Planned',
    progress: 0,
    totalPoints: 0,
    completedPoints: 0,
  },
  kpis: [],
}

/** Ensure a project always has valid arrays and objects to prevent .map() crashes */
function safeProject(project: Project | undefined | null): Project {
  if (!project) return EMPTY_PROJECT
  return {
    ...project,
    issues: Array.isArray(project.issues) ? project.issues : [],
    backlog: Array.isArray(project.backlog) ? project.backlog : [],
    kpis: Array.isArray(project.kpis) ? project.kpis : [],
    sprint: project.sprint || EMPTY_PROJECT.sprint,
  }
}

/** Ensure all projects in an array have valid data */
function safeProjects(projects: Project[]): Project[] {
  return projects.map(safeProject)
}
interface ProjectContextType {
  activeProject: Project
  projects: Project[]
  setActiveProject: (id: string) => void
  isGlobalView: boolean
  setIsGlobalView: (v: boolean) => void
  /** Replace projects with real Jira data */
  setRealProjects: (projects: Project[]) => void
  /** Update a single project's data (sprint, issues, backlog, kpis) */
  updateProjectData: (projectKey: string, data: Partial<Project>) => void
  /** Whether we're using real data or mock */
  isRealData: boolean
  /** Data mode: mock or live */
  dataMode: DataMode
  setDataMode: (mode: DataMode) => void
}
const ProjectContext = createContext<ProjectContextType>({
  activeProject: EMPTY_PROJECT,
  projects: [],
  setActiveProject: () => {},
  isGlobalView: false,
  setIsGlobalView: () => {},
  setRealProjects: () => {},
  updateProjectData: () => {},
  isRealData: false,
  dataMode: 'live',
  setDataMode: () => {},
})
export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const [dataMode, setDataModeState] = useState<DataMode>(() => {
    try {
      const stored = localStorage.getItem(DATA_MODE_KEY)
      if (stored === 'mock' || stored === 'live') return stored
    } catch {}
    return 'live'
  })
  const [mockProjectsList] = useState<Project[]>(mockProjects)
  const [liveProjects, setLiveProjects] = useState<Project[]>([])
  const [activeProjectId, setActiveProjectId] = useState(mockProjects[0].id)
  const [isGlobalView, setIsGlobalView] = useState(true)
  const [isRealData, setIsRealData] = useState(false)
  const rawProjects =
    dataMode === 'live' && liveProjects.length > 0
      ? liveProjects
      : mockProjectsList

  // Always ensure projects have valid arrays
  const projects = safeProjects(rawProjects)
  const setDataMode = useCallback(
    (mode: DataMode) => {
      setDataModeState(mode)
      try {
        localStorage.setItem(DATA_MODE_KEY, mode)
      } catch {}
      // Reset to first project of the new list
      if (mode === 'mock') {
        setActiveProjectId(mockProjects[0].id)
      } else if (liveProjects.length > 0) {
        setActiveProjectId(liveProjects[0].id)
      }
      setIsGlobalView(false)
    },
    [liveProjects],
  )
  const setActiveProject = useCallback((id: string) => {
    setActiveProjectId(id)
    setIsGlobalView(false)
  }, [])
  const setRealProjects = useCallback((realProjects: Project[]) => {
    if (realProjects.length > 0) {
      setLiveProjects(realProjects)
      setIsRealData(true)
      // Always select first real project when setting live data
      setActiveProjectId(realProjects[0].id)
    }
  }, [])
  const updateProjectData = useCallback(
    (projectKey: string, data: Partial<Project>) => {
      setLiveProjects((prev) =>
        prev.map((p) =>
          p.key === projectKey
            ? {
                ...p,
                ...data,
              }
            : p,
        ),
      )
    },
    [],
  )

  // Always return a safe project with valid arrays
  const activeProject = safeProject(
    projects.find((p) => p.id === activeProjectId) || projects[0],
  )
  return React.createElement(
    ProjectContext.Provider,
    {
      value: {
        activeProject,
        projects,
        setActiveProject,
        isGlobalView,
        setIsGlobalView,
        setRealProjects,
        updateProjectData,
        isRealData,
        dataMode,
        setDataMode,
      },
    },
    children,
  )
}
export function useProjectContext() {
  return useContext(ProjectContext)
}
